import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import * as React from 'react';
import styled from 'styled-components';
import { spacing2x, spacing1x } from '../../styles/spacing.js';
import { getAdornment } from '../../utils/adornment.js';
import { mergeProps } from '../../utils/react.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var _excluded = ["children", "endAdornment", "focusRef", "startAdornment"];
var StyledAdornment = styled.span.withConfig({
  displayName: "InputContainer__StyledAdornment",
  componentId: "sc-z8h8so-0"
})(["&&{align-items:center;display:flex;}"]);
var StartAdornment = styled(StyledAdornment).withConfig({
  displayName: "InputContainer__StartAdornment",
  componentId: "sc-z8h8so-1"
})(["&&{padding-inline-end:", ";}"], spacing2x);
var EndAdornment = styled(StyledAdornment).withConfig({
  displayName: "InputContainer__EndAdornment",
  componentId: "sc-z8h8so-2"
})(["&&{margin-inline-start:auto;padding-inline-start:", ";}"], spacing2x);
var getContainerStyles = function getContainerStyles(props) {
  var active = props.active,
    disabled = props.disabled,
    emailStyle = props.emailStyle,
    error = props.error,
    focused = props.focused,
    lightStyle = props.lightStyle,
    readOnly = props.readOnly,
    success = props.success,
    theme = props.theme;
  var shouldShowHoverStyles = !disabled && !error && !success && !focused && !active && !readOnly;
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    background: theme.neutralColors.white,
    borderColor: theme.neutralColors.silver,
    borderRadius: spacing1x,
    borderStyle: "solid",
    borderWidth: 1,
    color: theme.neutralColors.charcoal,
    fontSize: 14,
    padding: "5px ".concat(spacing2x),
    transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out"
  }, disabled ? {
    opacity: 0.5,
    cursor: "not-allowed",
    backgroundColor: theme.neutralColors.pearl
  } : {}), error ? {
    borderColor: theme.alertColors.error,
    color: theme.alertColors.error
  } : {}), success ? {
    borderColor: theme.alertColors.success,
    color: theme.alertColors.success
  } : {}), focused && (!error || !success) ? {
    outline: 0,
    background: theme.neutralColors.white,
    borderColor: theme.CTAColors.primary
  } : {}), shouldShowHoverStyles ? {
    ":hover:not(:focus)": {
      borderColor: theme.neutralColors.smoke
    }
  } : {}), emailStyle && {
    borderRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    paddingBottom: "calc(".concat(spacing1x, "/2)")
  }), lightStyle && _objectSpread(_objectSpread({
    borderColor: error ? theme.alertColors.error : "transparent",
    color: error ? theme.alertColors.error : theme.neutralColors.charcoal
  }, shouldShowHoverStyles && {
    ":hover:not(:focus)": {
      background: "#ECEEF0",
      borderColor: "#ECEEF0"
    }
  }), focused && (!error || !success) && {
    background: theme.neutralColors.white,
    borderColor: theme.CTAColors.primary
  }));
};
var StyledInputContainer = styled.div.withConfig({
  displayName: "InputContainer__StyledInputContainer",
  componentId: "sc-z8h8so-3"
})(function (props) {
  return _objectSpread(_objectSpread({}, getContainerStyles(props)), {}, {
    cursor: "text",
    display: "inline-flex",
    height: 32,
    position: "relative",
    ":not(._mntl-search &):focus-within": {
      "::before": {
        content: "''",
        position: "absolute",
        top: "-3px",
        right: "-3px",
        bottom: "-3px",
        left: "-3px",
        border: "1px solid",
        borderColor: props.theme.neutralColors.charcoal,
        borderRadius: "6px",
        pointerEvents: "none"
      },
      "::after": {
        content: "''",
        position: "absolute",
        top: "-4px",
        right: "-4px",
        bottom: "-4px",
        left: "-4px",
        border: "1px solid",
        borderColor: props.theme.neutralColors.white,
        borderRadius: "7px",
        pointerEvents: "none"
      }
    }
  });
});
var InputContainer = /*#__PURE__*/React.forwardRef(function Input(props, ref) {
  var children = props.children,
    endAdornment = props.endAdornment,
    focusRef = props.focusRef,
    startAdornment = props.startAdornment,
    rest = _objectWithoutProperties(props, _excluded);
  var defaultFocusRef = React.useRef(null);
  var startRef = React.useRef(null);
  var endRef = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1];
  var handleClick = function handleClick(e) {
    if (defaultFocusRef !== null && defaultFocusRef !== void 0 && defaultFocusRef.current && (e.currentTarget === e.target || e.currentTarget === startRef.current || e.currentTarget === endRef.current)) {
      var _focusable$;
      var focusable = defaultFocusRef === null || defaultFocusRef === void 0 ? void 0 : defaultFocusRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusRef !== null && focusRef !== void 0 && focusRef.current) focusRef.current.focus();else (_focusable$ = focusable[0]) === null || _focusable$ === void 0 ? void 0 : _focusable$.focus();
    }
  };
  var ICON_SIZE = 20;
  var startAddon = getAdornment(startAdornment, {
    size: ICON_SIZE
  });
  var endAddon = getAdornment(endAdornment, {
    size: ICON_SIZE
  });
  return /*#__PURE__*/jsxs(StyledInputContainer, _objectSpread(_objectSpread({}, mergeProps({
    "data-css-selector": "inputContainer",
    onBlur: function onBlur() {
      return setFocused(false);
    },
    onFocus: function onFocus() {
      return setFocused(true);
    },
    onClick: handleClick,
    ref: defaultFocusRef
  }, _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref
  }))), {}, {
    focused: focused,
    hasEndAdornment: !!props.endAdornment,
    children: [startAddon && /*#__PURE__*/jsx(StartAdornment, {
      ref: startRef,
      children: startAddon
    }), children, endAddon && /*#__PURE__*/jsx(EndAdornment, {
      ref: endRef,
      children: endAddon
    })]
  }));
});
InputContainer.displayName = "InputContainer";

export { InputContainer, getContainerStyles };
